import type { GetServerSideProps, NextPage } from 'next'
import type { CountryName, Locale } from '@model/locales'
import { deliveryCountries, KK_LANG_IDS } from '@model/locales'
import CartStateProvider from '@components/cart/State/state'
import logger from '@utils/logging'
import { getProductBySku } from '@api/products'
import type { Product } from '@model/product'
import axios from 'axios'
import dynamic from 'next/dynamic'
import type { CustomerAddress } from '@api/addressCustomer'
import { customerAddress, getDefaultAddressIdsByCountryName, getDefaultCustomerAddresses } from '@api/addressCustomer'
import { useRouter } from 'next/router'
import { SESSION_ID_COOKIE } from '@pages/api/auth'
import { isGloballyPricelist } from '@utils/pricelist-utils/app-properties'
import { Loader } from '@ui/components/Loader'
import React, { useEffect, useState } from 'react'
import { CwsGrid, CwsGridCol, CwsGridRow } from '@components/cws'
import type { Order } from '@model/order/Order'

const CartMain = dynamic(
  isGloballyPricelist() ? () => import('@components/Null') : () => import('@components/cart/CartMain'),
  {
    loading: () => null,
    ssr: false,
  }
)

interface OrderPageProps {
  giftWrapper?: Product
  defaultDeliveryAddrIds: Record<CountryName, number>
  customerAddressBook: CustomerAddress[]
}

const OrderPage: NextPage<OrderPageProps> = ({ giftWrapper, defaultDeliveryAddrIds, customerAddressBook }) => {
  const { locale } = useRouter()

  const [order, setOrder] = useState<Order | null>()
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setIsLoading(true)
    axios
      .post('/api/order', {
        locale,
        currencyCode: locale === 'sv' ? 'SEK' : 'EUR',
        deliveryCountryName: deliveryCountries[locale as Locale],
        ...(locale === 'sv' && {
          deliveryAddrId: defaultDeliveryAddrIds['Sweden'],
        }),
      })
      .then((response) => {
        if (response.data.order) {
          setOrder(response.data.order)
        }
      })
      .catch(() => setOrder(null))
      .finally(() => setIsLoading(false))
  }, [defaultDeliveryAddrIds, locale])

  if (order === undefined || isLoading)
    return (
      <CwsGrid width="fluid">
        <CwsGridRow>
          <CwsGridCol call={12} className="h-[30vh] flex justify-center">
            <div className="flex w-full h-[30vh] items-center justify-center absolute  animate-spin">
              <Loader />
            </div>
          </CwsGridCol>
        </CwsGridRow>
      </CwsGrid>
    )

  return (
    <CartStateProvider order={order} giftWrapper={giftWrapper} customerAddressBook={customerAddressBook}>
      <CartMain />
    </CartStateProvider>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ locale, req }) => {
  const languageId = KK_LANG_IDS[locale as Locale]
  const sessionId = req.cookies[SESSION_ID_COOKIE]

  try {
    const giftWrapper = await getProductBySku('WSBOX', languageId)
    const defaultDeliveryAddrIds = await getDefaultAddressIdsByCountryName(languageId)

    return {
      props: {
        giftWrapper,
        defaultDeliveryAddrIds,
        customerAddressBook: sessionId
          ? await customerAddress(languageId, sessionId)
          : await getDefaultCustomerAddresses(languageId),
      },
    }
  } catch (e) {
    if (e instanceof Error) {
      logger.error('Error', { message: e.message })
    }
    return {
      props: {},
    }
  }
}

export default OrderPage
